import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { doc, setDoc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db, auth } from "../../lib/firebase";
import { useAuthContext } from "../../contexts/AuthContext";
import { Loader2, User, Phone, Mail, AtSign, CheckCircle } from "lucide-react";

export default function CompleteProfile() {
  const { user, loading: authLoading } = useAuthContext();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    username: "",
    fullName: "",
    phoneNumber: "",
    email: user?.email || "",
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [checkingUsername, setCheckingUsername] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
      return;
    }

    if (user?.email) {
      setFormData(prev => ({ ...prev, email: user.email }));
    }

    const checkExistingProfile = async () => {
      if (!user?.uid) return;

      const profileRef = doc(db, "studentProfiles", user.uid);
      const profileSnap = await getDoc(profileRef);

      if (profileSnap.exists() && profileSnap.data()?.completed) {
        navigate("/progress");
      }
    };

    checkExistingProfile();
  }, [user, authLoading, navigate]);

  const validateUsername = (username: string): string | null => {
    if (!username) {
      return "กรุณากรอก Username";
    }
    
    if (username.length < 3 || username.length > 20) {
      return "Username ต้องมีความยาว 3-20 ตัวอักษร";
    }
    
    if (!/^[a-z0-9_]+$/.test(username)) {
      return "Username ใช้ได้เฉพาะ a-z, 0-9, และ _ เท่านั้น";
    }
    
    return null;
  };

  const checkUsernameAvailability = async (username: string): Promise<boolean> => {
    if (!username) return false;

    const usernameQuery = query(
      collection(db, "studentProfiles"),
      where("username", "==", username.toLowerCase())
    );
    
    const snapshot = await getDocs(usernameQuery);
    return snapshot.empty;
  };

  const handleUsernameChange = async (value: string) => {
    const lowercaseValue = value.toLowerCase();
    setFormData(prev => ({ ...prev, username: lowercaseValue }));
    
    const validationError = validateUsername(lowercaseValue);
    if (validationError) {
      setUsernameError(validationError);
      return;
    }
    
    setCheckingUsername(true);
    setUsernameError("");
    
    try {
      const isAvailable = await checkUsernameAvailability(lowercaseValue);
      if (!isAvailable) {
        setUsernameError("Username นี้ถูกใช้งานแล้ว กรุณาเลือก Username อื่น");
      }
    } catch (err) {
      console.error("Error checking username:", err);
    } finally {
      setCheckingUsername(false);
    }
  };

  const validatePhoneNumber = (phone: string): boolean => {
    const phoneRegex = /^[0-9]{9,10}$/;
    return phoneRegex.test(phone.replace(/[-\s]/g, ""));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!user?.uid) {
      setError("ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    const usernameValidation = validateUsername(formData.username);
    if (usernameValidation) {
      setError(usernameValidation);
      return;
    }

    if (!formData.fullName.trim()) {
      setError("กรุณากรอกชื่อ-นามสกุล");
      return;
    }

    if (!formData.phoneNumber.trim()) {
      setError("กรุณากรอกเบอร์โทรศัพท์");
      return;
    }

    if (!validatePhoneNumber(formData.phoneNumber)) {
      setError("รูปแบบเบอร์โทรศัพท์ไม่ถูกต้อง");
      return;
    }

    if (usernameError) {
      setError("กรุณาแก้ไข Username ให้ถูกต้อง");
      return;
    }

    setLoading(true);

    try {
      const isUsernameAvailable = await checkUsernameAvailability(formData.username);
      if (!isUsernameAvailable) {
        setError("Username นี้ถูกใช้งานแล้ว กรุณาเลือก Username อื่น");
        setLoading(false);
        return;
      }

      const profileRef = doc(db, "studentProfiles", user.uid);
      await setDoc(profileRef, {
        uid: user.uid,
        username: formData.username.toLowerCase(),
        fullName: formData.fullName.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        email: formData.email,
        completed: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      const userRef = doc(db, "users", user.uid);
      await setDoc(userRef, {
        uid: user.uid,
        email: formData.email,
        name: formData.fullName.trim(),
        displayName: formData.fullName.trim(),
        role: "student",
        purchasedCourses: [],
        updatedAt: new Date(),
      }, { merge: true });

      navigate("/progress");
    } catch (err: any) {
      console.error("Error saving profile:", err);
      setError("เกิดข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองใหม่อีกครั้ง");
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-blue-100 mx-auto mb-4 flex items-center justify-center">
            <User className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">ยินดีต้อนรับ!</h1>
          <p className="text-gray-600">กรุณากรอกข้อมูลเพื่อเริ่มต้นใช้งาน</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Username */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Username
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <AtSign className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => handleUsernameChange(e.target.value)}
                placeholder="username"
                className={`w-full pl-10 pr-10 py-3 border-2 rounded-xl focus:outline-none transition-colors ${
                  usernameError
                    ? "border-red-300 focus:border-red-500"
                    : formData.username && !checkingUsername && !usernameError
                    ? "border-green-300 focus:border-green-500"
                    : "border-gray-200 focus:border-blue-500"
                }`}
                disabled={loading}
              />
              {checkingUsername && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              )}
              {!checkingUsername && formData.username && !usernameError && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
              )}
            </div>
            {usernameError && (
              <p className="text-red-600 text-sm mt-1">{usernameError}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              ใช้ได้เฉพาะ a-z, 0-9, และ _ (3-20 ตัวอักษร)
            </p>
          </div>

          {/* Full Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              ชื่อ-นามสกุล
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={formData.fullName}
                onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                placeholder="ชื่อจริง นามสกุล"
                className="w-full pl-10 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors"
                disabled={loading}
              />
            </div>
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              เบอร์โทรศัพท์
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Phone className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                placeholder="0812345678"
                className="w-full pl-10 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors"
                disabled={loading}
              />
            </div>
          </div>

          {/* Email (Read-only) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              อีเมล
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="email"
                value={formData.email}
                readOnly
                className="w-full pl-10 py-3 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-600 cursor-not-allowed"
              />
            </div>
            <p className="text-gray-500 text-xs mt-1">
              อีเมลจากบัญชีที่ใช้เข้าสู่ระบบ (แก้ไขไม่ได้)
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || checkingUsername || !!usernameError}
            className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:bg-blue-700 hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                กำลังบันทึก...
              </span>
            ) : (
              "บันทึกข้อมูล"
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            ข้อมูลของคุณจะถูกเก็บรักษาอย่างปลอดภัย
          </p>
        </div>
      </div>
    </div>
  );
}
